import React, { useState, useEffect, useRef, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  termsContent: {
    height: "60vh",
    overflow: "auto",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    "& img": {
      maxWidth: "100%"
    },
    "& p": {
      marginBottom: theme.spacing(1),
      lineHeight: 1.6
    },
    "& h1, & h2, & h3": {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(1)
    }
  },
  scrollHint: {
    display: "block",
    marginBottom: theme.spacing(1),
    fontStyle: "italic"
  },
  dialogTitle: {
    textAlign: "center",
    paddingBottom: 0
  },
  dialogActions: {
    justifyContent: "space-between",
    padding: theme.spacing(2, 3)
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "60vh"
  }
}));

const TermsOfUseModal = () => {
  const classes = useStyles();
  const { isAuth, user, handleLogout } = useContext(AuthContext);
  const [termsHtml, setTermsHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [open, setOpen] = useState(false);
  const [hasAccepted, setHasAccepted] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    if (isAuth && user && user.id && !user.acceptedTermsAt && !hasAccepted) {
      const fetchTerms = async () => {
        try {
          setLoading(true);
          const { data } = await api.get("/settings/terms");
          if (data.html) {
            setTermsHtml(data.html);
            setOpen(true);
          }
        } catch (err) {
          // Se nao houver arquivo de termos, nao mostra o modal
          console.log("Termos de uso nao encontrados - ignorando modal");
        } finally {
          setLoading(false);
        }
      };
      fetchTerms();
    }
  }, [isAuth, user?.id, user?.acceptedTermsAt]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      setScrolledToBottom(true);
    }
  };

  const handleConfirm = async () => {
    if (!accepted) {
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/auth/accept-terms");
      setHasAccepted(true);
      setOpen(false);
    } catch (err) {
      toastError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleExit = () => {
    handleLogout();
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
      }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Typography variant="h5" component="span">
          {i18n.t("termsOfUse.title")}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        ) : (
          <>
            <div
              ref={contentRef}
              className={classes.termsContent}
              onScroll={handleScroll}
              dangerouslySetInnerHTML={{ __html: termsHtml }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  disabled={!scrolledToBottom}
                  color="primary"
                />
              }
              label={i18n.t("termsOfUse.agreeLabel")}
            />

            {!scrolledToBottom && (
              <Typography
                variant="caption"
                color="textSecondary"
                className={classes.scrollHint}
              >
                {i18n.t("termsOfUse.scrollHint")}
              </Typography>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button onClick={handleExit} color="secondary" variant="outlined">
          {i18n.t("termsOfUse.exitButton")}
        </Button>
        <Button
          onClick={handleConfirm}
          color="primary"
          variant="contained"
          disabled={!accepted || submitting}
        >
          {submitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            i18n.t("termsOfUse.confirmButton")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TermsOfUseModal;
